import React, { useEffect, useState } from "react";
import { X, RefreshCw } from "lucide-react";
import moneyCurrency from "../assets/money-currency.png";
import { useSuperAdmin } from "../context/SuperAdminContext";

const formatAmount = (amount) =>
  (amount ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusStyles = {
  success: { bg: "#e6f7ef", color: "#1a9e5c", label: "Success" },
  payoutGenerated: { bg: "#eef2ff", color: "#3b3fa6", label: "Payout Generated" },
  pending: { bg: "#fff7e6", color: "#b26a00", label: "Pending" },
  failed: { bg: "#fef2f2", color: "#e53935", label: "Failed" },
};

const StatusBadge = ({ status }) => {
  const s = statusStyles[status] || { bg: "#f3f4f6", color: "#374151", label: status || "-" };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {s.label}
    </span>
  );
};

// Confirmation modal for retrying a failed incentive (sets status -> pending)
const ConfirmUpdateModal = ({ isOpen, onCancel, onConfirm, isSubmitting }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Confirm Update"
    >
      <div
        className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Confirm Update</h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div style={styles.warningBanner}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
          <span>
            Are you sure you want to update the status of this payment to{" "}
            <strong style={{ color: "#b26a00" }}>pending</strong>? This action cannot be
            undone.
          </span>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            style={styles.cancelBtn}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            style={styles.confirmBtn}
          >
            {isSubmitting ? "Updating..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function IncentiveHistoryModal({ isOpen, onClose, workerId }) {
  const { fetchMonthlyInsentive, updatePaymentStatus } = useSuperAdmin();

  const [records, setRecords] = useState([]);
  const [totalPayout, setTotalPayout] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- refresh / confirm-to-pending state ---
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedIncentiveId, setSelectedIncentiveId] = useState(null);
  const [updatingIds, setUpdatingIds] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !workerId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetchMonthlyInsentive(workerId);
        const d = res?.data;

        if (d) {
          setRecords(Array.isArray(d.records) ? d.records : []);
          setTotalPayout(d.totalIncentivePayout ?? 0);
        }
      } catch (err) {
        console.error("Error loading incentive data:", err);
        setError("Failed to load incentive history.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, workerId, fetchMonthlyInsentive]);

  const openConfirmModal = (recordId) => {
    setSelectedIncentiveId(recordId);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    if (updatingIds[selectedIncentiveId]) return; // don't close mid-request
    setShowConfirmModal(false);
    setSelectedIncentiveId(null);
  };

  const confirmSetPending = async () => {
    if (!selectedIncentiveId) return;
    try {
      setUpdatingIds((prev) => ({ ...prev, [selectedIncentiveId]: true }));

      // --- API call: always sends incentive id + status "pending" ---
      const response = await updatePaymentStatus({
        incentiveId: selectedIncentiveId,
        status: "pending",
      });

      if (response?.data?.success ?? response?.success ?? true) {
        setRecords((prev) =>
          prev.map((r) =>
            r._id === selectedIncentiveId ? { ...r, paymentStatus: "pending" } : r
          )
        );
      } else {
        setError(response?.data?.message || response?.message || "Failed to update status.");
      }
    } catch (err) {
      console.error("Error updating incentive status:", err);
      setError(
        err?.response?.data?.message || "Error updating payment status. Please try again."
      );
    } finally {
      setUpdatingIds((prev) => {
        const next = { ...prev };
        delete next[selectedIncentiveId];
        return next;
      });
      setShowConfirmModal(false);
      setSelectedIncentiveId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Incentive Payout History"
    >
      <div
        className="relative w-full max-w-5xl max-h-[85vh] overflow-y-auto rounded-xl bg-white p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex items-center gap-4 justify-center mb-6">
          <button onClick={onClose} className="text-2xl absolute left-0">
            ‹
          </button>
          <h2 className="text-xl font-bold text-center">Incentive Payout</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-0 p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Total Banner */}
        <div style={styles.totalBanner}>
          <div style={styles.totalIcon}>
            <img src={moneyCurrency} alt="Money Currency" />
          </div>
          <div>
            <div style={styles.totalLabel}>Total Incentive Payout</div>
            <div style={styles.totalAmount}>₹ {formatAmount(totalPayout)}</div>
          </div>
        </div>

        {/* Error */}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* Table */}
        {loading ? (
          <div style={styles.skeleton} />
        ) : records.length === 0 ? (
          <div style={styles.emptyState}>No incentive records found.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
<table style={styles.table}>
  <thead>
    <tr>
      <th style={{ ...styles.th, textAlign: "center" }}>Start Date</th>
      <th style={{ ...styles.th, textAlign: "center" }}>End Date</th>
      <th style={{ ...styles.th, textAlign: "center" }}>Total Shifts</th>
      <th style={{ ...styles.th, textAlign: "center" }}>Total Hours</th>
      <th style={{ ...styles.th, textAlign: "center" }}>Amount (₹)</th>
      <th style={{ ...styles.th, textAlign: "center" }}>Status</th>
      <th style={{ ...styles.th, textAlign: "center" }}>Payout Generated Date</th>
    </tr>
  </thead>
  <tbody>
    {records.map((r) => (
      <tr key={r._id}>
        <td style={{ ...styles.td, textAlign: "center" }}>{formatDate(r.cycle_start_date)}</td>
        <td style={{ ...styles.td, textAlign: "center" }}>{formatDate(r.cycle_end_date)}</td>
        <td style={{ ...styles.td, textAlign: "center" }}>{r.shiftCount ?? 0}</td>
        <td style={{ ...styles.td, textAlign: "center" }}>{r.total_working_hours ?? 0}</td>
        <td style={{ ...styles.td, textAlign: "center" }}>{r.totalIncentive}</td>
        <td style={{ ...styles.td, textAlign: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {r.totalIncentive === 0 ? "-" : <StatusBadge status={r.paymentStatus} />}
            {r.paymentStatus === "failed" && r.totalIncentive != 0 && (
              <button
                type="button"
                onClick={() => openConfirmModal(r._id)}
                disabled={!!updatingIds[r._id]}
                aria-label="Retry payment"
                title="Retry payment"
                style={styles.refreshBtn}
              >
                <RefreshCw
                  className="h-3.5 w-3.5"
                  style={{
                    animation: updatingIds[r._id]
                      ? "spin 1s linear infinite"
                      : "none",
                  }}
                />
              </button>
            )}
          </span>
        </td>
        <td style={{ ...styles.td, textAlign: "center" }}>{formatDate(r.createdAt)}</td>
      </tr>
    ))}
  </tbody>
</table>
          </div>
        )}
      </div>

      <ConfirmUpdateModal
        isOpen={showConfirmModal}
        onCancel={closeConfirmModal}
        onConfirm={confirmSetPending}
        isSubmitting={!!updatingIds[selectedIncentiveId]}
      />
    </div>
  );
}

const styles = {
  totalBanner: {
    background: "#e6f7ef",
    margin: "0 0 20px 0",
    borderRadius: 12,
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  totalIcon: {
    width: 40,
    height: 40,
    background: "#fff",
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 1,
  },
  totalAmount: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a9e5c",
    letterSpacing: "-0.5px",
    fontVariantNumeric: "tabular-nums",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#f9fafb",
    color: "#40495e",
    fontWeight: 600,
    fontSize: 12,
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
    color: "#111827",
    whiteSpace: "nowrap",
  },
  errorBanner: {
    background: "#fef2f2",
    color: "#e53935",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 16,
    border: "1px solid #fecaca",
  },
  emptyState: {
    textAlign: "center",
    padding: "30px 0",
    color: "#6b7280",
    fontSize: 14,
  },
  skeleton: {
    height: 200,
    borderRadius: 11,
    background:
      "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    padding: 2,
    borderRadius: 4,
  },
  warningBanner: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    background: "#fffaf0",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13.5,
    color: "#374151",
    lineHeight: 1.5,
  },
  cancelBtn: {
    flex: 1,
    padding: "10px 0",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
  confirmBtn: {
    flex: 1,
    padding: "10px 0",
    borderRadius: 8,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
};